import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { TicketStatusRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSlug } from "@/hooks/useSlug";
import { type FormValues, TicketStatusForm } from "@/modules/ticket-statuses/components/TicketStatusForm";
import { ticketStatusMutations } from "@/queries/ticket-statuses/mutations";
import { useTicketStatusQueries } from "@/queries/ticket-statuses/queryOptions";
import { routes } from "@/routes";

export function EditTicketStatusPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const statusId = id ?? "";
  const queries = useTicketStatusQueries();
  const statusesQuery = useQuery(queries.list());
  const statusQuery = useQuery(queries.details(statusId));
  const updateStatus = ticketStatusMutations.useUpdateTicketStatus();
  const error = statusesQuery.error ?? statusQuery.error;

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (statusesQuery.isFetching || statusQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  if (!statusQuery.data) {
    return <ErrorPage status={404} />;
  }

  return (
    <TicketStatusForm
      isEditMode
      isSubmitting={updateStatus.isPending}
      ticketStatuses={statusesQuery.data?.items ?? []}
      defaultFormValues={{
        name: statusQuery.data.name,
        color: statusQuery.data.color,
        isDefault: statusQuery.data.isDefault,
        type: statusQuery.data.type,
      }}
      onSubmit={(values) => {
        updateStatus.mutate(
          { id: statusId, data: toRequest(values) },
          { onSuccess: () => void navigate(routes.ticketStatuses.list({ slug })) },
        );
      }}
    />
  );
}

function toRequest(values: FormValues): TicketStatusRequest {
  return {
    name: values.name,
    nameTranslations: [],
    color: values.color || "",
    isDefault: values.isDefault,
    type: values.type,
  };
}
